import React, { useEffect, useRef } from 'react'
import { prefersReducedMotion } from './verificationTheme'
import { rampDone, rampDurationFor, rampValue, restingRamp, retarget, type Ramp } from './counterRamp'

// A total that counts up to its value rather than jumping to it. The policy
// is counterRamp.ts; this is the loop that draws it.
//
// The chart's performance contract holds here too (see VerificationChart):
// the frame loop writes one text node and never calls setState, and it stops
// altogether once the figure has arrived, so a paused video costs nothing.

const format = (value: number): string => Math.floor(value).toLocaleString('en-GB')

export interface LiveCounterProps {
  /** The true total. The figure shown climbs to it and never passes it. */
  value: number
  /** Held where it is: a failed check stops the count dead. */
  frozen?: boolean
  style?: React.CSSProperties
}

export default function LiveCounter({ value, frozen = false, style }: LiveCounterProps) {
  const node = useRef<HTMLSpanElement | null>(null)
  // Starts ON the total, not at zero: opening the panel must not replay
  // checking that already happened as if it were happening now.
  const ramp = useRef<Ramp>(restingRamp(value, 0))
  const lastChangeMs = useRef<number | null>(null)
  // The text React renders never changes after mount, so a re-render cannot
  // put the new total on screen ahead of the climb to it.
  const initialText = useRef(format(value)).current

  useEffect(() => {
    const span = node.current
    if (!span) return undefined

    const now = performance.now()
    if (frozen) {
      // Stop where the count stands, without finishing the climb.
      ramp.current = restingRamp(rampValue(ramp.current, now), now)
      span.textContent = format(ramp.current.to)
      return undefined
    }
    if (prefersReducedMotion()) {
      ramp.current = restingRamp(value, now)
      span.textContent = format(value)
      return undefined
    }

    if (value !== ramp.current.to) {
      const gap = lastChangeMs.current === null ? null : now - lastChangeMs.current
      lastChangeMs.current = now
      ramp.current = retarget(ramp.current, value, now, rampDurationFor(gap))
    }

    let frame = 0
    const step = () => {
      const t = performance.now()
      span.textContent = format(rampValue(ramp.current, t))
      if (!rampDone(ramp.current, t)) frame = requestAnimationFrame(step)
    }
    step()
    return () => cancelAnimationFrame(frame)
  }, [value, frozen])

  return (
    <span ref={node} style={{ fontVariantNumeric: 'tabular-nums', ...style }}>
      {initialText}
    </span>
  )
}
